import React, { Suspense, useContext, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { AuthProvider } from './context/AuthContext';
import { TimezoneProvider } from './context/TimezoneContext';
import { LanguageProvider } from './context/LanguageContext';
import LanguageContext from './context/LanguageContext';
import { getTheme } from './theme/theme';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import PageLoader from './components/PageLoader';
import PlatformPresenceTracker from './components/PlatformPresenceTracker';
import PageActivityTracker from './components/PageActivityTracker';

const Login = lazy(() => import('./pages/Login'));
const StudentLogin = lazy(() => import('./pages/StudentLogin'));
const TrialLogin = lazy(() => import('./pages/TrialLogin'));
const TeacherLogin = lazy(() => import('./pages/TeacherLogin'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const CoordinatorLogin = lazy(() => import('./pages/CoordinatorLogin'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Users = lazy(() => import('./pages/Users'));
const TrialUsers = lazy(() => import('./pages/TrialUsers'));
const Curriculum = lazy(() => import('./pages/Curriculum'));
const CoursesManagement = lazy(() => import('./pages/CoursesManagement'));
const LecturePresentation = lazy(() => import('./pages/LecturePresentation'));
const Groups = lazy(() => import('./pages/Groups'));
const Payments = lazy(() => import('./pages/Payments'));
const StudentMonthlyPayments = lazy(() => import('./pages/StudentMonthlyPayments'));
const GroupMonthlyPayments = lazy(() => import('./pages/GroupMonthlyPayments'));
const TeacherMonthlyPayments = lazy(() => import('./pages/TeacherMonthlyPayments'));
const Expenses = lazy(() => import('./pages/Expenses'));
const GroupProfitability = lazy(() => import('./pages/GroupProfitability'));
const CourseStatistics = lazy(() => import('./pages/CourseStatistics'));
const Inbox = lazy(() => import('./pages/Inbox'));
const SalesKpis = lazy(() => import('./pages/SalesKpis'));
const SalesCommunicationDashboard = lazy(() => import('./pages/SalesCommunicationDashboard'));
const SectionDashboardRoute = lazy(() => import('./pages/SectionDashboardRoute'));
const Marketing = lazy(() => import('./pages/Marketing'));
const TeacherAccounts = lazy(() => import('./pages/TeacherAccounts'));
const Settings = lazy(() => import('./pages/Settings'));
const MeetingSettings = lazy(() => import('./pages/MeetingSettings'));
const AiSettings = lazy(() => import('./pages/AiSettings'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const InboxAutoReplySettings = lazy(() => import('./pages/InboxAutoReplySettings'));
const InboxWindowRenewal = lazy(() => import('./pages/InboxWindowRenewal'));
const Layout = lazy(() => import('./components/Layout'));
const Students = lazy(() => import('./pages/Students'));
const Teachers = lazy(() => import('./pages/Teachers'));
const Coordinators = lazy(() => import('./pages/Coordinators'));
const CoordinatorBulkAssign = lazy(() => import('./pages/CoordinatorBulkAssign'));
const CoordinatorDistribution = lazy(() => import('./pages/CoordinatorDistribution'));
const AdminRoles = lazy(() => import('./pages/AdminRoles'));
const FreeSessions = lazy(() => import('./pages/FreeSessions'));
const Tools = lazy(() => import('./pages/Tools'));
const AdsLanding = lazy(() => import('./pages/AdsLanding'));
const Leads = lazy(() => import('./pages/Leads'));
const MeetingRoom = lazy(() => import('./pages/MeetingRoom'));
const PublicMeetingJoin = lazy(() => import('./pages/PublicMeetingJoin'));
const StudentLectures = lazy(() => import('./pages/StudentLectures'));
const TeacherLectures = lazy(() => import('./pages/TeacherLectures'));
const StudentGroups = lazy(() => import('./pages/StudentGroups'));
const MyTrialSessions = lazy(() => import('./pages/MyTrialSessions'));
const StudentPortfolioEditor = lazy(() => import('./pages/StudentPortfolioEditor'));
const PublicStudentProfile = lazy(() => import('./pages/PublicStudentProfile'));
const StudentProjectsPlay = lazy(() => import('./pages/StudentProjectsPlay'));
const CustomersOpinionManager = lazy(() => import('./pages/CustomersOpinionManager'));
const ProjectsShowcaseManager = lazy(() => import('./pages/ProjectsShowcaseManager'));
const RoomSessionActivities = lazy(() => import('./pages/RoomSessionActivities'));
const UserPlatformActivity = lazy(() => import('./pages/UserPlatformActivity'));
const HomeworkQueue = lazy(() => import('./pages/HomeworkQueue'));
const SessionsCalendar = lazy(() => import('./pages/SessionsCalendar'));
const Land1Page = lazy(() => import('./pages/Land1Page'));
const Land2Page = lazy(() => import('./pages/Land2Page'));
const Land3Page = lazy(() => import('./pages/Land3Page'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const HrEmployees = lazy(() => import('./pages/HrEmployees'));
const HrPayroll = lazy(() => import('./pages/HrPayroll'));
const HrJobs = lazy(() => import('./pages/HrJobs'));
const HrApplications = lazy(() => import('./pages/HrApplications'));
const HrInterview = lazy(() => import('./pages/HrInterview'));
const HrEmployeeMeetings = lazy(() => import('./pages/HrEmployeeMeetings'));
const GroupChat = lazy(() => import('./pages/GroupChat'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const CertificateTemplates = lazy(() => import('./pages/CertificateTemplates'));
const CertificateTemplateDesigner = lazy(() => import('./pages/CertificateTemplateDesigner'));
const CertificateGeneration = lazy(() => import('./pages/CertificateGeneration'));
const IssuedCertificates = lazy(() => import('./pages/IssuedCertificates'));
const Surveys = lazy(() => import('./pages/Surveys'));
const PublicSurvey = lazy(() => import('./pages/PublicSurvey'));
const PublicCertificateVerify = lazy(() => import('./pages/PublicCertificateVerify'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const HelpAdmin = lazy(() => import('./pages/HelpAdmin'));
const InteractiveLabs = lazy(() => import('./pages/InteractiveLabs'));
const InteractiveLabPlayer = lazy(() => import('./pages/InteractiveLabPlayer'));
const InteractiveLabsAdmin = lazy(() => import('./pages/InteractiveLabsAdmin'));
const InteractiveLabEditor = lazy(() => import('./pages/InteractiveLabEditor'));
const LiveSync = lazy(() => import('./pages/LiveSync'));
const CodingStudio = lazy(() => import('./pages/CodingStudio'));
const MyBadges = lazy(() => import('./pages/MyBadges'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const LeaderboardAdmin = lazy(() => import('./pages/LeaderboardAdmin'));
const BadgeShare = lazy(() => import('./pages/BadgeShare'));
const BadgeGuide = lazy(() => import('./pages/BadgeGuide'));
const BadgesAdmin = lazy(() => import('./pages/BadgesAdmin'));
const StudentEngagementOverview = lazy(() => import('./pages/StudentEngagementOverview'));
const LectureSessionEvaluations = lazy(() => import('./pages/LectureSessionEvaluations'));
const LectureSessionAnalytics = lazy(() => import('./pages/LectureSessionAnalytics'));
const StudentLectureEvaluations = lazy(() => import('./pages/StudentLectureEvaluations'));

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const cacheLtr = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

const UsersRoute = () => {
  const [searchParams] = useSearchParams();
  if (searchParams.get('role') === 'trial') {
    return <Navigate to="/trial-users" replace />;
  }
  return <Users />;
};

const AppContent = () => {
  const { direction } = useContext(LanguageContext);
  const theme = React.useMemo(() => getTheme(direction), [direction]);
  const emotionCache = direction === 'rtl' ? cacheRtl : cacheLtr;

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
      <Router>
        <PlatformPresenceTracker />
        <PageActivityTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/"
              element={
                <LandingPage />
              }
            />
            <Route path="/ads" element={<AdsLanding />} />
            <Route
              path="/home"
              element={
                <LandingPage />
              }
            />
            <Route path="/land1" element={<Land1Page />} />
            <Route path="/land2" element={<Land2Page />} />
            <Route path="/land3" element={<Land3Page />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/survey/:token" element={<PublicSurvey />} />
            <Route path="/verify/:verificationId" element={<PublicCertificateVerify />} />
            <Route path="/verify" element={<PublicCertificateVerify />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/play" element={<StudentProjectsPlay />} />
            <Route path="/play/:slug/:projectId" element={<StudentProjectsPlay />} />
            <Route path="/badge/:token" element={<BadgeShare />} />
            <Route path="/badges-guide" element={<BadgeGuide />} />
            <Route
              path="/login/trial"
              element={
                <PublicRoute>
                  <TrialLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/login/student"
              element={
                <PublicRoute>
                  <StudentLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/login/teacher"
              element={
                <PublicRoute>
                  <TeacherLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/login/coordinator"
              element={
                <PublicRoute>
                  <CoordinatorLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/login/admin"
              element={
                <PublicRoute>
                  <AdminLogin />
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/:sectionId"
              element={
                <PrivateRoute>
                  <Layout>
                    <SectionDashboardRoute />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <PrivateRoute>
                  <Layout>
                    <UsersRoute />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/trial-users"
              element={
                <PrivateRoute>
                  <Layout>
                    <TrialUsers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/users/trial"
              element={<Navigate to="/trial-users" replace />}
            />
            <Route
              path="/students"
              element={
                <PrivateRoute>
                  <Layout>
                    <Students />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teachers"
              element={
                <PrivateRoute>
                  <Layout>
                    <Teachers />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/coordinators"
              element={
                <PrivateRoute>
                  <Layout>
                    <Coordinators />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/coordinator-bulk-assign"
              element={
                <PrivateRoute>
                  <Layout>
                    <CoordinatorBulkAssign />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/coordinator-distribution"
              element={
                <PrivateRoute>
                  <Layout>
                    <CoordinatorDistribution />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin-roles"
              element={
                <PrivateRoute>
                  <Layout>
                    <AdminRoles />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/curriculum"
              element={
                <PrivateRoute>
                  <Layout>
                    <Curriculum />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/courses-management"
              element={
                <PrivateRoute>
                  <Layout>
                    <CoursesManagement />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/lecture-presentation/:lectureId"
              element={
                <PrivateRoute>
                  <LecturePresentation />
                </PrivateRoute>
              }
            />
            <Route
              path="/lecture/:id"
              element={
                <PrivateRoute>
                  <LecturePresentation />
                </PrivateRoute>
              }
            />
            <Route
              path="/groups"
              element={
                <PrivateRoute>
                  <Layout>
                    <Groups />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/course-statistics"
              element={
                <PrivateRoute>
                  <Layout>
                    <CourseStatistics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/my-lectures"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentLectures />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teaching-lectures"
              element={
                <PrivateRoute>
                  <Layout>
                    <TeacherLectures />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/my-trial-sessions"
              element={
                <PrivateRoute>
                  <Layout>
                    <MyTrialSessions />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/my-groups"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentGroups />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/meeting/:id"
              element={
                <PrivateRoute>
                  <MeetingRoom />
                </PrivateRoute>
              }
            />
            <Route path="/meeting/public/:id" element={<PublicMeetingJoin />} />
            <Route path="/u/:slug" element={<PublicStudentProfile />} />
            <Route
              path="/free-sessions"
              element={
                <PrivateRoute>
                  <Layout>
                    <FreeSessions />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/my-portfolio"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentPortfolioEditor />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/my-badges"
              element={
                <PrivateRoute>
                  <Layout>
                    <MyBadges />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Leaderboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/leaderboard-admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <LeaderboardAdmin />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/badges-admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <BadgesAdmin />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <PrivateRoute>
                  <Layout>
                    <Payments />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/student-monthly-payments"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentMonthlyPayments />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/group-monthly-payments"
              element={
                <PrivateRoute>
                  <Layout>
                    <GroupMonthlyPayments />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher-monthly-payments"
              element={
                <PrivateRoute>
                  <Layout>
                    <TeacherMonthlyPayments />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <PrivateRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/group-profitability"
              element={
                <PrivateRoute>
                  <Layout>
                    <GroupProfitability />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/teacher-accounts"
              element={
                <PrivateRoute>
                  <Layout>
                    <TeacherAccounts />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/meeting-settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <MeetingSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ai-settings"
              element={
                <PrivateRoute>
                  <Layout>
                    <AiSettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/api-keys"
              element={
                <PrivateRoute>
                  <Layout>
                    <ApiKeys />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox-auto-reply"
              element={
                <PrivateRoute>
                  <Layout>
                    <InboxAutoReplySettings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox-window-renewal"
              element={
                <PrivateRoute>
                  <Layout>
                    <InboxWindowRenewal />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/tools"
              element={
                <PrivateRoute>
                  <Layout>
                    <Tools />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <PrivateRoute>
                  <Layout>
                    <Leads />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-communication"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesCommunicationDashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <PrivateRoute>
                  <Layout>
                    <Inbox />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/surveys"
              element={
                <PrivateRoute>
                  <Layout>
                    <Surveys />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/help"
              element={
                <PrivateRoute>
                  <Layout>
                    <HelpCenter />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/help-admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <HelpAdmin />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/interactive-labs"
              element={
                <PrivateRoute>
                  <Layout>
                    <InteractiveLabs />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/interactive-labs/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <InteractiveLabPlayer />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/live-sync/team"
              element={
                <PrivateRoute>
                  <Layout>
                    <LiveSync />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route path="/live-sync" element={<Navigate to="/live-sync/team" replace />} />
            <Route
              path="/coding-studio"
              element={
                <PrivateRoute>
                  <Layout>
                    <CodingStudio />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/interactive-labs-admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <InteractiveLabsAdmin />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/interactive-labs-admin/new"
              element={
                <PrivateRoute>
                  <Layout>
                    <InteractiveLabEditor />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/interactive-labs-admin/edit/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <InteractiveLabEditor />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-kpis"
              element={
                <PrivateRoute>
                  <Layout>
                    <SalesKpis />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/marketing"
              element={
                <PrivateRoute>
                  <Layout>
                    <Marketing />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/employees"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrEmployees />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/payroll"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrPayroll />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/jobs"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrJobs />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/applications"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrApplications />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/employee-meetings"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrEmployeeMeetings />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/hr/interview/:applicationId"
              element={
                <PrivateRoute>
                  <Layout>
                    <HrInterview />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/coordinator/groups"
              element={
                <PrivateRoute>
                  <Navigate to="/groups" replace />
                </PrivateRoute>
              }
            />
            <Route
              path="/coordinator/groups/:groupId/chat"
              element={
                <PrivateRoute>
                  <Layout>
                    <GroupChat />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/certificates/templates"
              element={
                <PrivateRoute>
                  <Layout>
                    <CertificateTemplates />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/certificates/templates/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <CertificateTemplateDesigner />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/certificates/issued"
              element={
                <PrivateRoute>
                  <Layout>
                    <IssuedCertificates />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/certificates/generate"
              element={
                <PrivateRoute>
                  <Layout>
                    <CertificateGeneration />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/customers-opinion"
              element={
                <PrivateRoute>
                  <Layout>
                    <CustomersOpinionManager />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/projects-showcase"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProjectsShowcaseManager />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/room-session-activities"
              element={
                <PrivateRoute>
                  <Layout>
                    <RoomSessionActivities />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/user-platform-activity"
              element={
                <PrivateRoute>
                  <Layout>
                    <UserPlatformActivity />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/student-engagement"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentEngagementOverview />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/lecture-session-evaluations"
              element={
                <PrivateRoute>
                  <Layout>
                    <LectureSessionEvaluations />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/lecture-session-analytics"
              element={
                <PrivateRoute>
                  <Layout>
                    <LectureSessionAnalytics />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/student-lecture-evaluations"
              element={
                <PrivateRoute>
                  <Layout>
                    <StudentLectureEvaluations />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/sessions-calendar"
              element={
                <PrivateRoute>
                  <Layout>
                    <SessionsCalendar />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/homework-queue"
              element={
                <PrivateRoute>
                  <Layout>
                    <HomeworkQueue />
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
        </Suspense>
        <ToastContainer position="top-center" rtl={direction === 'rtl'} />
      </Router>
      </ThemeProvider>
    </CacheProvider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <TimezoneProvider>
          <AppContent />
        </TimezoneProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
